import React, { useState, useEffect } from "react";
import VisibilitySensor from "react-visibility-sensor";
import styles from "./index.module.scss";
import * as icons from "../../config/Icons";
import Tooltip from "@material-ui/core/Tooltip";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

export default function BarChartStacked({
  title,
  data,
  colors,
  fontColors,
  legends,
  labelX,
  labelY,
  showTotal,
  totalSuffix,
  animDuration = 1000,
  animPredelay = 0,
  innerText = true,
  maxHeight,
  country = false,
  labelXSize = "small",
  showTooltip = false,
  ...props
}) {
  const { t } = useTranslation();
  const [totals, setTotals] = useState([]);
  const [maxTotal, setMaxTotal] = useState([]);
  const [showValues, setShowValues] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    updateChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => {
        setShowValues(true);
      }, animPredelay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  // only animate on component visibility
  const onVisibilityChange = isVisible => {
    if (isVisible) {
      setIsVisible(true);
    }
  };

  const updateChart = () => {
    let maxTotal = -Infinity;
    let totals = [];
    data.forEach(item => {
      let itemTotal = 0;
      item.values.forEach(v => {
        itemTotal += v;
      });
      totals.push(itemTotal);
      if (itemTotal > maxTotal) {
        maxTotal = itemTotal;
      }
    });
    setMaxTotal(maxTotal);
    setTotals(totals);
  };

  const getTranslated = string => {
    let currentString = string;
    const regex = /\bt\("([A-Za-z0-9-:.]*)\b"\)/g;
    let translationCode = currentString.match(regex);
    if (translationCode && translationCode.length > 0) {
      translationCode.forEach(string => {
        currentString = currentString.replace(string, eval(string));
      });
    }
    return currentString;
  };

  return (
    <div className={styles.container}>
      {title && (
        <div className={styles.title}>
          <strong>{title}</strong>
        </div>
      )}
      <div className={styles.barCharts}>
        <ul className={styles.barChartsList}>
          <li className={styles.barChartsHeader}>
            <div
              className={`${
                labelXSize === "large" ? styles.labelXWider : styles.labelX
              }`}
            >
              {labelX}
            </div>
            <div className={[styles.labelY, styles.show].join(" ")}>
              {labelY}
            </div>
            {showTotal && (
              <div className={styles.total}>{t("dashboard.coach.total")}</div>
            )}
          </li>
        </ul>
        <VisibilitySensor onChange={onVisibilityChange}>
          <ul
            className={styles.barChartsList}
            style={{ maxHeight: `${maxHeight ? maxHeight : "auto"}` }}
          >
            {data.map((item, index) => {
              const flagIcon = item.label + "FlagIcon";
              return (
                <li className={styles.barChartsItem} key={index}>
                  <div
                    className={`${
                      labelXSize === "large"
                        ? styles.labelXWider
                        : styles.labelX
                    }`}
                  >
                    {country && <img src={icons[flagIcon]} alt={item.label} />}
                    {getTranslated(item.label)}
                  </div>
                  <div
                    className={[styles.labelY].join(" ")}
                    style={{
                      marginBottom: `${
                        labelXSize === "large" ? "0px" : "1rem"
                      }`,
                    }}
                  >
                    <Tooltip
                      title={
                        showTooltip ? (
                          <TooltipContent
                            data={item}
                            title={title && title}
                            legends={legends}
                          />
                        ) : (
                          ""
                        )
                      }
                      placement="bottom-start"
                      PopperProps={{
                        popperOptions: {
                          modifiers: {
                            offset: {
                              enabled: true,
                              offset: "0, -20px",
                            },
                          },
                        },
                      }}
                    >
                      <div
                        style={{ transition: `${animDuration}ms width` }}
                        className={[
                          styles.labelYInner,
                          showValues ? styles.show : "",
                        ].join(" ")}
                      >
                        {item.values.map((value, _index) => {
                          let barWidth = (value / maxTotal) * 100;
                          return (
                            <div
                              key={_index}
                              className={styles.bar}
                              style={{
                                width: `${barWidth}%`,
                                backgroundColor: `${colors[_index]}`,
                              }}
                            >
                              {innerText && (
                                <span
                                  style={{
                                    transitionDuration: `${animDuration}ms`,
                                    color: `${fontColors[_index]}`,
                                  }}
                                >
                                  {value}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </Tooltip>
                  </div>
                  {showTotal && (
                    <div className={styles.total}>
                      <strong>
                        {`${totals[index]} `}
                        {totalSuffix}
                      </strong>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </VisibilitySensor>
      </div>
      <div className={styles.footer}>
        <div
          className={styles.legends}
          style={{
            marginLeft: `${labelXSize === "large" ? "165px" : "0"}`,
          }}
        >
          <ul className={styles.legendsList}>
            {legends.map((legend, index) => {
              return (
                <li className={styles.legendItem} key={index}>
                  <div
                    className={styles.legendColor}
                    style={{ backgroundColor: `${colors[index]}` }}
                  ></div>
                  <span>{legend}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

function TooltipContent({ data, title, legends, ...props }) {
  return (
    <div className={styles.tooltipContainer}>
      {title && <span className={styles.bold}>{title} </span>}
      <span className={styles.bold}>{data.label} </span>
      <span>
        {" "}
        {legends[0]} : <span>{parseFloat(data.values[0]).toFixed(2)}</span>
      </span>
      <span>
        {" "}
        {legends[1]} : <span>{parseFloat(data.values[1]).toFixed(2)}</span>
      </span>
      <span>
        {" "}
        {legends[2]} : <span>{parseFloat(data.values[2]).toFixed(2)}</span>
      </span>
      <span>
        {" "}
        {legends[3]} : <span>{parseFloat(data.values[3]).toFixed(2)}</span>
      </span>
      {data.maxTotal && (
        <span className={styles.total}>
          {" "}
          {t("dashboard.coach.total")} :{" "}
          <span>{parseFloat(data.maxTotal).toFixed(2)} mins</span>
        </span>
      )}
    </div>
  );
}
